import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import type { CheckboxColor } from '../types';
import { CheckboxColors } from '../types';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { GzTooltip } from '../../gzTooltip';
import { isElementOverflowed, OverflowDirection } from '../../../utils/htmlElement.utils';

@customElement('gz-checkbox')
export class GzCheckboxWebComponent extends FormFieldInput<boolean> {
  /**
   * The default checkbox color theme is blue
   */
  @property({ type: String, attribute: true, reflect: true })
  color: CheckboxColor = CheckboxColors.PRIMARY;

  @property({ type: Boolean, reflect: true })
  checked = false;

  @property({ type: Boolean, reflect: true })
  stopEmitEvents = false;

  @property({ type: Boolean, reflect: true })
  disabled = false;

  /**
   * This property should be true to show the UI indeterminate state for nested checkboxes
   */
  @property({ type: Boolean, reflect: true })
  indeterminate = false;

  @property({ type: String, reflect: true })
  label = '';

  @property({ type: String, reflect: true })
  value = '';

  @property({ type: String, reflect: true })
  name = '';

  /**
   * If this property is set to true:
   * 1. show an ellipsis if the text is longer than the container
   * 2. display the tooltip only if the content overflows the parent
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isEllipsis = false;

  @query('input')
  protected checkbox!: HTMLInputElement;

  public setFormValue(value: boolean) {
    this.checked = value;
    if (!this.stopEmitEvents) {
      this.emitCheckboxChangeEvent();
    }
  }

  public getFormValue(): boolean {
    return this.checked;
  }

  public setEnabled(isEnabled: boolean): void {
    this.disabled = !isEnabled;
  }

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  constructor() {
    super();
    this.addEventListener('click', this.handleClick);
  }

  render() {
    return html`
      <div class="checkbox ${this.invalid ? CheckboxColors.ERROR : this.color}">
        <input
          type="checkbox"
          id="checkbox"
          class="checkbox--input"
          name="${this.name}"
          .checked="${this.checked}"
          ?disabled="${this.disabled}"
          .indeterminate="${this.indeterminate}"
          .value="${this.value}"
        />
        <label for="checkbox" class="checkbox--label"> ${this.renderLabelText()} </label>
      </div>
    `;
  }

  private renderLabelText() {
    if (this.isEllipsis) {
      return html`<gz-tooltip isEllipsis tooltipText="${this.label}">
        <span
          class="checkbox--value"
          @mouseenter="${this.handleMouseEnter}"
          @mousemove="${GzTooltip.align}"
          @mouseleave="${GzTooltip.hide}"
          part="checkbox-label"
          >${this.label}</span
        ><slot></slot>
      </gz-tooltip>`;
    }

    return html`<span
        class="checkbox--value"
        @mouseenter="${this.handleMouseEnter}"
        @mousemove="${GzTooltip.align}"
        @mouseleave="${GzTooltip.hide}"
        part="checkbox-label"
        >${this.label}</span
      ><slot></slot>`;
  }

  private handleClick(e) {
    if (this.readonly) {
      return;
    }
    if (this.indeterminate) {
      this.indeterminate = false;
    }

    e.preventDefault();
    this.toggleChecked();
  }

  private toggleChecked() {
    this.checked = !this.checked;
    if (!this.stopEmitEvents) {
      this.emitCheckboxChangeEvent();
    }
  }

  private emitCheckboxChangeEvent() {
    /**
     * Emit the change event with element details
     */
    const changeEvent = new CustomEvent('change', {
      detail: {
        checked: this.checked,
        indeterminate: this.indeterminate,
        label: this.label,
        value: this.value,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(changeEvent);
  }

  private handleMouseEnter(event: MouseEvent) {
    if (!this.shadowRoot) {
      return;
    }

    const element = this.shadowRoot.querySelector('span.checkbox--value') as HTMLElement;
    if (!isElementOverflowed(element, OverflowDirection.HORIZONTAL)) {
      return;
    }
    const tooltipText = this.label;

    if (tooltipText) {
      GzTooltip.show(tooltipText, event);
    }
  }
}
